import { withTranslation } from '@application/i18n/with_translation';
import { DateDisplayFormats, DateService } from '@application/time/date.service';
import { Button, DialogAlert, DialogModal, Flex, Text } from '@cellula/react';
import { tokens } from '@cellula/react-theme-patient';
import { TopBar } from '@component/top_bar.component';
import { useNavigationGuard } from '@cortex-shim/navigation_guard_context';
import { RouterService } from '@cortex-shim/router.service';
import type { MessagingMetaData } from '@feature/cases/case_messaging_model';
import { Chat } from '@feature/chat/chat.component';
import { IdentitySession } from '@feature/identity_session/_identity_session.component';
import { useResolve, useResolveController } from '@visiba/instantiation';
import { Transition } from 'history';
import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { ViewAnonymousChatController } from '../view_anonymous_chat.controller';
import { AnonymousChatCommunicator } from './_anonymous_chat_communicator';

export function AnonymousChat() {
	const { caseId } = useParams();
	const controller = useResolveController(ViewAnonymousChatController);
	const routerService = useResolve(RouterService);
	const navigationGuard = useNavigationGuard();
	const dateService = useResolve(DateService);

	const [showConfirmation, setShowConfirmation] = useState(false);
	const [showConversationClosed, setShowConversationClosed] = useState(false);
	const [shouldBlockNavigation, setShouldBlockNavigation] = useState(true);
	const [messagingMetaData, setMessagingMetaData] = useState<MessagingMetaData | null>(null);

	const blockedTransitionRef = useRef<Transition | null>(null);
	const angularDialogResolverRef = useRef<((value: boolean) => void) | null>(null);

	// === FETCH CONVERSATION METADATA ===
	useEffect(() => {
		if (!caseId) return;

		const fetchMetadata = async () => {
			const metadata = await controller.fetchConversationMetadata(caseId);
			setMessagingMetaData(metadata);
		};

		fetchMetadata();
	}, [caseId, controller]);

	// === BROWSER WARNING (for refresh/back events from browser) ===
	// Store the handler so we can remove it programmatically
	const beforeUnloadHandlerRef = useRef<((e: BeforeUnloadEvent) => void) | null>(null);
	useEffect(() => {
		if (!shouldBlockNavigation) return;

		function handleBeforeUnload(e: BeforeUnloadEvent) {
			e.preventDefault();
			e.returnValue = '';
		}
		beforeUnloadHandlerRef.current = handleBeforeUnload;
		window.addEventListener('beforeunload', handleBeforeUnload);

		return () => {
			window.removeEventListener('beforeunload', handleBeforeUnload);
			beforeUnloadHandlerRef.current = null;
		};
	}, [shouldBlockNavigation]);

	// === ANGULAR ROUTER BLOCKING ===
	// If using the Angular shell, this handles navigation blocking for the Angular router
	useEffect(() => {
		if (!navigationGuard || !caseId) return;

		// Tell Angular whether blocking is active
		navigationGuard.setGuard(() => shouldBlockNavigation);

		// Register dialog handler - Angular calls this when trying to navigate away
		navigationGuard.setShowDialogHandler(() => {
			return new Promise<boolean>((resolve) => {
				angularDialogResolverRef.current = resolve;
				setShowConfirmation(true);
			});
		});

		return () => {
			navigationGuard.clearGuard();
		};
	}, [shouldBlockNavigation, navigationGuard, caseId]);

	// === REACT ROUTER BLOCKING ===
	// If NOT using the Angular shell this manages navigation blocking for React Router
	useEffect(() => {
		if (!shouldBlockNavigation) return;

		const unblock = routerService.history.block((tx) => {
			// Store the transition so we can use the path on confirm
			blockedTransitionRef.current = tx;
			setShowConfirmation(true);
		});

		return () => {
			unblock();
		};
	}, [shouldBlockNavigation, routerService.history]);

	if (!caseId) {
		controller.goToScreen('start');
		return null;
	}

	function handleConnectionError() {
		controller.handleSocketError(caseId, 'connectionError');
	}

	function handleUnauthorized() {
		controller.handleSocketError(caseId, 'unauthorized');
	}

	// biome-ignore lint/correctness/useHookAtTopLevel: suppressed due to migration - should be fixed
	const chatCommunicatorRef = useRef<AnonymousChatCommunicator>();
	if (chatCommunicatorRef.current == null || chatCommunicatorRef.current.connectionId !== caseId) {
		chatCommunicatorRef.current = new AnonymousChatCommunicator(caseId);
	}

	async function handleAbort() {
		if (messagingMetaData?.completedTimestamp !== null && messagingMetaData?.completedTimestamp !== undefined) {
			// If the case is already completed, just sign out and redirect without showing the confirmation dialog
			setShouldBlockNavigation(false);
			await new Promise((resolve) => setTimeout(resolve, 0));

			if (beforeUnloadHandlerRef.current) {
				window.removeEventListener('beforeunload', beforeUnloadHandlerRef.current);
				beforeUnloadHandlerRef.current = null;
			}
			controller.signOutAndRedirect();

			return;
		}
		setShowConfirmation(true);
	}

	async function handleConfirmLeave() {
		await controller.closeCase(caseId!);

		const wasNavigating = angularDialogResolverRef.current !== null || blockedTransitionRef.current !== null;

		if (wasNavigating) {
			setShouldBlockNavigation(false);

			// Wait for shouldBlockNavigation state to propagate to navigation guards before redirecting
			await new Promise((resolve) => setTimeout(resolve, 0));

			// Get the destination path if it was a React Router navigation
			if (blockedTransitionRef.current) {
				await controller.signOutAndRedirect(blockedTransitionRef.current.location.pathname);
			} else {
				await controller.signOutAndRedirect();
			}
		} else {
			setShouldBlockNavigation(false);
			setShowConversationClosed(true);
			setShowConfirmation(false);
		}
	}

	async function onEndConversationFromExpired() {
		setShouldBlockNavigation(false);
		await controller.closeCase(caseId!);
	}

	async function closeConversationClosedDialog(open: boolean) {
		if (!open) {
			await controller.signOutAndRedirect();
			setShowConversationClosed(false);
		}
	}

	function handleCancelLeave() {
		// User cancelled - just close the dialog and stay
		setShowConfirmation(false);
		blockedTransitionRef.current = null;

		// If Angular was waiting, tell it to block the navigation
		if (angularDialogResolverRef.current) {
			angularDialogResolverRef.current(false);
			angularDialogResolverRef.current = null;
		}
	}

	function handleMarkAsCompleted(): void {
		if (messagingMetaData !== null) {
			setMessagingMetaData({ ...messagingMetaData, completedTimestamp: dateService.newDate() });
		}
		setShouldBlockNavigation(false);
	}

	return (
		<IdentitySession.Root>
			<IdentitySession.Warning beforeSignOut={onEndConversationFromExpired} />
			{/* Dialog shown after successfully ending the chat */}
			<DialogModal.Root open={showConversationClosed} onOpenChange={closeConversationClosedDialog}>
				<DialogModal.Title>{withTranslation('View.AnonymousChat.Dialog.ConversationClosed.Title')}</DialogModal.Title>
				<DialogModal.Description>
					{withTranslation('View.AnonymousChat.Dialog.ConversationClosed.Description')}
				</DialogModal.Description>
				<DialogModal.Footer>
					<Flex direction='row' justify='flex-end' gap={tokens.spacing.x1}>
						<DialogModal.Close asChild>
							<Button variant='ghost'>{withTranslation('Common.Button.Close')}</Button>
						</DialogModal.Close>
					</Flex>
				</DialogModal.Footer>
			</DialogModal.Root>

			{/* Dialog asking user to confirm leaving the chat */}
			<DialogAlert
				isOpen={showConfirmation}
				onOpenChange={(open) => {
					if (!open) {
						handleCancelLeave();
					}
				}}
				header={withTranslation('View.AnonymousChat.Dialog.LeaveChat.Title')}
				description={withTranslation('View.AnonymousChat.Dialog.LeaveChat.Description')}
				abortText={withTranslation('View.AnonymousChat.Dialog.LeaveChat.Button.Abort')}
				successText={withTranslation('View.AnonymousChat.Dialog.LeaveChat.Button.Continue')}
				onSuccess={handleConfirmLeave}
			/>
			<TopBar.Content
				title='Chat'
				onAbort={handleAbort}
				abortText={withTranslation('View.AnonymousChat.Screen.Chat.EndChat')}
				skipAbortDialog={true}
				goBack={false}
			/>
			<Flex direction='column' style={{ flex: 1, minHeight: '60vh', height: '100%' }}>
				<Chat
					connectedId={caseId}
					communicator={chatCommunicatorRef.current}
					messagingMetaData={messagingMetaData}
					onConnectionError={handleConnectionError}
					onUnauthorized={handleUnauthorized}
					onMarkAsCompleted={handleMarkAsCompleted}
					hideFileUpload={true}
					forceHideActions={messagingMetaData?.completedTimestamp != null}
					showBottomBanner={
						messagingMetaData?.completedTimestamp != null ? (
							<Text variant='labelXSmallRegular' color={tokens.color.content.muted}>
								{withTranslation('PW.Component.Visits.MessagingInfo.CaseCompleted', {
									dateTime: messagingMetaData.completedTimestamp.format(
										dateService.getLocaleSpecificDateFormatTemplate(DateDisplayFormats.FULL_DISPLAY_IN_CURRENT_YEAR_NO_WEEKDAY),
									),
								})}
							</Text>
						) : null
					}
				/>
			</Flex>
		</IdentitySession.Root>
	);
}
